import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import {
  SearchFilter,
  IFilterResponse,
} from "src/app/infrastructure/iFilter-component";
import { MatDialog } from "@angular/material";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { Router } from "@angular/router";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { MenuService } from "src/app/services/menu.service";
import { ReportViewerDialogComponent } from "../../dialogs/report-viewer/report-viewer-dialog/report-viewer-dialog.component";
import { BillingInvoiceManualService } from "../../../services/billing/billing-invoice-manual/billing-invoice-manual.service";
import { BillingInvoiceManualSearchItem } from "../../../model/billing/billing-invoice-manual-search-item";
import { InvoiceType } from "src/app/enums/invoice-type.enum";
import { ConfirmDialogService } from "../../../services/confirm-dialog.service";
import { of } from "rxjs";
import { BillingManualInvoiceLogDialogComponent } from "../../dialogs/billing-manual-invoice-log-dialog/billing-manual-invoice-log-dialog.component";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DatePipe } from "@angular/common";

interface ColumnDefinition {
  field: string;
  title: string;
  visible: boolean;
  order: number;
  width: number;
}

@Component({
  selector: "app-billing-invoice-manual-search",
  templateUrl: "./billing-invoice-manual-search.component.html",
  styleUrls: ["./billing-invoice-manual-search.component.css"],
  providers: [DatePipe],
})
export class BillingInvoiceManualSearchComponent implements OnInit, OnDestroy {
  searchFilters: SearchFilter[];
  dataSource: BillingInvoiceManualSearchItem[] = [];
  allItems: BillingInvoiceManualSearchItem[] = [];
  @ViewChild("searchFilter", { static: false })
  searchFilter: SearchFilterComponent;
  @ViewChild("pager", { static: true }) pager: PagerControlComponent;

  loading: boolean;
  columnFilters: { [field: string]: string } = {};

  private sessionColumnsKey = "billing-invoice-manual-search.columns.v1";
  private sessionSortKey = "billing-invoice-manual-search.sort.v1";
  private sessionColumnWidthsKey = "billing-invoice-manual-search.column-widths.v1";

  private sortField: string = null;
  private sortDirection: "asc" | "desc" = "asc";
  private dragSourceField: string = null;
  private resizeListeners: { move: EventListener; up: EventListener } = null;

  columnsDefinition: ColumnDefinition[] = [
    { field: "invoiceNumber", title: "Número", visible: true, order: 0, width: 140 },
    { field: "invoiceDate", title: "Fecha", visible: true, order: 1, width: 120 },
    { field: "amount", title: "Importe", visible: true, order: 2, width: 120 },
    { field: "alicuotsColumn", title: "Iva", visible: true, order: 3, width: 100 }
  ];

  get availableColumns(): ColumnDefinition[] {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): ColumnDefinition[] {
    return this.columnsDefinition
      .filter(c => c.visible)
      .sort((a, b) => a.order - b.order);
  }

  exportColumns: string[] = [
    "InvoiceNumberComplete",
    "InvoiceDate",
    "Amount",
    "AlicuotsColumn",
    // "BillingGroupName",
    // "BillingGroupFrom",
    // "BillingGroupTo",
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private notificationToastService: NotificationToastService,
    private menuService: MenuService,
    private confirmDialogService: ConfirmDialogService,
    private billingInvoiceManualService: BillingInvoiceManualService,
    private datePipe: DatePipe
  ) {
    this.menuService.setMenuTitle("Facturas manuales", "");
  }

  ngOnInit() {
    this.restoreColumns();
    this.restoreSort();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  openLogViewDialog(
    billingInvoiceManualSearchItem: BillingInvoiceManualSearchItem
  ): void {
    this.dialog.open(BillingManualInvoiceLogDialogComponent, {
      width: "50%",
      data: billingInvoiceManualSearchItem.InvoiceId,
    });
  }

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = [...pagedItems];
    this.loading = false;
  }

  onEndSearch(response: IFilterResponse<BillingInvoiceManualSearchItem>) {
    this.allItems = response.items || [];
    this.searchFilters = response.searchFilters;
    this.refreshGrid();
  }

  onStartSearch() {
    this.loading = true;
  }

  isColumnVisible(field: string): boolean {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.visible : false;
  }

  setColumnVisibility(field: string, visible: boolean) {
    const col = this.columnsDefinition.find(c => c.field === field);
    if (col) {
      col.visible = visible;
      this.saveColumns();
      this.refreshGrid();
    }
  }

  showAllColumns() {
    this.columnsDefinition.forEach(c => (c.visible = true));
    this.saveColumns();
    this.refreshGrid();
  }

  private saveColumns() {
    const state = this.columnsDefinition.map(c => ({ field: c.field, visible: c.visible, order: c.order }));
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(state));
  }

  private restoreColumns() {
    const raw = sessionStorage.getItem(this.sessionColumnsKey);
    if (!raw) { return; }
    try {
      const state: { field: string; visible: boolean; order: number }[] = JSON.parse(raw);
      state.forEach(s => {
        const col = this.columnsDefinition.find(c => c.field === s.field);
        if (col) {
          col.visible = s.visible;
          col.order = s.order;
        }
      });
    } catch { }
  }

  sortByColumn(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
    this.saveSort();
    this.refreshGrid();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) { return "unfold_more"; }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  private sortAllData(items: BillingInvoiceManualSearchItem[]): BillingInvoiceManualSearchItem[] {
    if (!this.sortField) { return items; }
    const dir = this.sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = this.getColumnFilterValue(a, this.sortField);
      const vb = this.getColumnFilterValue(b, this.sortField);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }

  private saveSort() {
    sessionStorage.setItem(this.sessionSortKey, JSON.stringify({ field: this.sortField, direction: this.sortDirection }));
  }

  private restoreSort() {
    const raw = sessionStorage.getItem(this.sessionSortKey);
    if (!raw) { return; }
    try {
      const s = JSON.parse(raw);
      this.sortField = s.field;
      this.sortDirection = s.direction;
    } catch { }
  }

  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.refreshGrid();
  }

  private applyColumnFilters(items: BillingInvoiceManualSearchItem[]): BillingInvoiceManualSearchItem[] {
    return items.filter(item => {
      return Object.keys(this.columnFilters).every(field => {
        const filterVal = this.normalizeFilterValue(this.columnFilters[field]);
        if (!filterVal) { return true; }
        const cellVal = this.normalizeFilterValue(this.getColumnFilterValue(item, field));
        return cellVal.includes(filterVal);
      });
    });
  }

  private getColumnFilterValue(item: BillingInvoiceManualSearchItem, field: string): string {
    switch (field) {
      case "invoiceNumber": return item.InvoiceNumberComplete || "";
      case "invoiceDate": return this.datePipe.transform(item.InvoiceDate, "dd/MM/yyyy") || "";
      case "amount": return item.Amount !== null && item.Amount !== undefined ? item.Amount.toString() : "";
      case "alicuotsColumn": return item.AlicuotsColumn || "";
      // case "billingGroupName": return item.BillingGroupName || "";
      // case "billingGroupFrom": return this.datePipe.transform(item.BillingGroupFrom, "dd/MM/yyyy") || "";
      // case "billingGroupTo": return this.datePipe.transform(item.BillingGroupTo, "dd/MM/yyyy") || "";
      default: return "";
    }
  }

  private normalizeFilterValue(value: string): string {
    if (!value) { return ""; }
    return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  private refreshGrid() {
    const filtered = this.applyColumnFilters(this.allItems);
    const sorted = this.sortAllData(filtered);
    this.pager.setItems(sorted);
  }

  getColumnWidth(field: string): number {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.width : 150;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const col = this.columnsDefinition.find(c => c.field === field);
    const startWidth = col ? col.width : 150;

    this.removeResizeListeners();

    const moveHandler: EventListener = (e: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      if (col) { col.width = newWidth; }
    };
    const upHandler: EventListener = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
    this.resizeListeners = { move: moveHandler, up: upHandler };
  }

  private removeResizeListeners() {
    if (this.resizeListeners) {
      document.removeEventListener("mousemove", this.resizeListeners.move);
      document.removeEventListener("mouseup", this.resizeListeners.up);
      this.resizeListeners = null;
    }
  }

  private saveColumnWidths() {
    const widths: { [field: string]: number } = {};
    this.columnsDefinition.forEach(c => (widths[c.field] = c.width));
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(widths));
  }

  private restoreColumnWidths() {
    const raw = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!raw) { return; }
    try {
      const widths: { [field: string]: number } = JSON.parse(raw);
      this.columnsDefinition.forEach(c => {
        if (widths[c.field] !== undefined) { c.width = widths[c.field]; }
      });
    } catch { }
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    this.dragSourceField = field;
    event.dataTransfer.effectAllowed = "move";
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (!this.dragSourceField || this.dragSourceField === targetField) { return; }
    const visible = this.orderedColumnsDefinition;
    const fromIdx = visible.findIndex(c => c.field === this.dragSourceField);
    const toIdx = visible.findIndex(c => c.field === targetField);
    if (fromIdx < 0 || toIdx < 0) { return; }
    moveItemInArray(visible, fromIdx, toIdx);
    visible.forEach((c, i) => {
      const col = this.columnsDefinition.find(x => x.field === c.field);
      if (col) { col.order = i; }
    });
    this.saveColumns();
    this.dragSourceField = null;
  }

  onHeaderDragEnd() {
    this.dragSourceField = null;
  }

  isCredit(billingInvoiceSearchItem: BillingInvoiceManualSearchItem) {
    return billingInvoiceSearchItem.InvoiceTypeId === InvoiceType.Credit;
  }

  redirectCreateInvoiceCredit(
    billingInvoiceSearchItem: BillingInvoiceManualSearchItem
  ) {
    const next$ = billingInvoiceSearchItem.HasCreditNote
      ? this.confirmDialogService.confirm(
          "Nota de crédito",
          "Ya existen notas de crédito generadas para esta factura. Desea continuar?",
          "Sí",
          "No"
        )
      : of(true);

    next$.subscribe((value) => {
      if (value) {
        this.redirectInvoicecredit(billingInvoiceSearchItem);
      }
    });
  }

  redirectInvoicecredit(
    billingInvoiceSearchItem: BillingInvoiceManualSearchItem = null
  ) {
    if (billingInvoiceSearchItem) {
      this.router.navigate([
        "billing-invoice-manual-credit-edit",
        billingInvoiceSearchItem.InvoiceId,
      ]);
    } else {
      this.router.navigate(["billing-invoice-manual-credit-edit"]);
    }
  }

  redirectCreateInvoice() {
    this.router.navigate(["billing-invoice-manual-edit", InvoiceType.Invoice]);
  }

  redirectCreateDebit() {
    this.router.navigate(["billing-invoice-manual-edit", InvoiceType.Debit]);
  }

  async sendInvoiceMail(billingInvoiceSearchItem: BillingInvoiceManualSearchItem) {
    billingInvoiceSearchItem.Loading = true;
    try {
      await this.billingInvoiceManualService.sendInvoiceMail(billingInvoiceSearchItem.InvoiceId);
      this.notificationToastService.success("El mail fue enviado correctamente.", "Facturación");
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Facturación"
      );
    } finally {
      billingInvoiceSearchItem.Loading = false;
    }
  }

  async viewReport(
    billingInvoiceManualSearchItem: BillingInvoiceManualSearchItem
  ) {
    try {
      billingInvoiceManualSearchItem.Loading = true;

      const reportViewer =
        await this.billingInvoiceManualService.getBillingInvoiceReport(
          billingInvoiceManualSearchItem.InvoiceId
        );

      this.dialog.open(ReportViewerDialogComponent, {
        width: "90%",
        height: "600px",
        data: reportViewer,
      });
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Facturación"
      );
    } finally {
      billingInvoiceManualSearchItem.Loading = false;
    }
  }
}
